/**
 * BreakevenChart Component
 *
 * Displays a breakeven chart showing:
 * - Revenue line (from origin, slope = avg price per kg)
 * - Total cost line (starts at fixed costs, slope = variable cost per kg)
 * - Breakeven intersection point
 * - Current performance marker
 *
 * Part of US-P7-003: Breakeven Analysis
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ReferenceDot,
  ResponsiveContainer
} from 'recharts'

/**
 * Format currency for axis/tooltip
 *
 * @param {number} value - Value to format
 * @returns {string} Formatted currency string
 */
function formatCurrency(value) {
  if (value >= 1000) {
    return `£${(value / 1000).toFixed(1)}k`
  }
  return `£${value.toFixed(0)}`
}

/**
 * Format kg for axis
 *
 * @param {number} value - Value in kg
 * @returns {string} Formatted string
 */
function formatKg(value) {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}t`
  }
  return `${value.toFixed(0)}kg`
}

/**
 * Custom tooltip component
 */
function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-neutral-900 mb-2">{formatKg(label)}</p>
      {payload.map((entry, index) => (
        <div key={index} className="flex items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-neutral-600">{entry.name}</span>
          </div>
          <span className="font-medium text-neutral-900">
            {formatCurrency(entry.value)}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * Generate chart data points for revenue and cost lines
 *
 * @param {Object} chartData - Chart data from API
 * @returns {Array} Data points for Recharts
 */
function generateChartData(chartData) {
  if (!chartData) return []

  const { fixed_costs, variable_cost_per_kg, avg_price_per_kg, current_point, breakeven_point } = chartData

  // Determine the max kg to show (150% of max between current and breakeven, or a reasonable default)
  const maxKg = Math.max(
    (current_point?.kg || 0) * 1.5,
    (breakeven_point?.kg || 0) * 1.5,
    1000 // minimum
  )

  // Generate data points
  const points = []
  const step = maxKg / 20 // 20 points

  for (let kg = 0; kg <= maxKg; kg += step) {
    points.push({
      kg: Math.round(kg),
      revenue: kg * avg_price_per_kg,
      totalCost: fixed_costs + (kg * variable_cost_per_kg)
    })
  }

  return points
}

/**
 * BreakevenChart Component
 *
 * @param {Object} props
 * @param {Object} props.chartData - Chart data from useBreakeven hook
 * @param {boolean} [props.isLoading] - Loading state
 * @param {string} [props.className] - Additional CSS classes
 */
export default function BreakevenChart({
  chartData,
  isLoading = false,
  className = ''
}) {
  // Generate chart data points
  const data = generateChartData(chartData)

  // Extract key points
  const breakevenPoint = chartData?.breakeven_point
  const currentPoint = chartData?.current_point
  const fixedCosts = chartData?.fixed_costs || 0
  const status = chartData?.status

  // Determine if we can show the chart
  const canShowChart = data.length > 0 && chartData?.avg_price_per_kg > 0

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Breakeven Analysis</span>
          {status === 'ok' && breakevenPoint && (
            <span className="text-sm font-normal text-neutral-500">
              Breakeven at {formatKg(breakevenPoint.kg)}
            </span>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent>
        {isLoading ? (
          <Skeleton className="h-80 w-full" />
        ) : !canShowChart ? (
          <div className="flex items-center justify-center h-80 text-neutral-500">
            <div className="text-center">
              <p className="font-medium">Unable to display chart</p>
              <p className="text-sm mt-1">
                {status === 'zero_contribution'
                  ? 'No sales data available for this period'
                  : status === 'negative_contribution'
                    ? 'Negative contribution margin'
                    : 'Insufficient data'
                }
              </p>
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />

              <XAxis
                dataKey="kg"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatKg}
                label={{
                  value: 'Quantity (kg)',
                  position: 'insideBottom',
                  offset: -5,
                  style: { fontSize: 12, fill: '#9ca3af' }
                }}
              />

              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#6b7280' }}
                tickFormatter={formatCurrency}
                label={{
                  value: 'Amount (GBP)',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12, fill: '#9ca3af' }
                }}
              />

              <Tooltip content={<CustomTooltip />} />

              <Legend
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
                iconType="line"
              />

              {/* Fixed costs reference line */}
              <ReferenceLine
                y={fixedCosts}
                stroke="#9ca3af"
                strokeDasharray="5 5"
                label={{
                  value: `Fixed: ${formatCurrency(fixedCosts)}`,
                  position: 'left',
                  style: { fontSize: 10, fill: '#9ca3af' }
                }}
              />

              {/* Revenue line */}
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#10b981"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, stroke: '#10b981', strokeWidth: 2 }}
              />

              {/* Total cost line */}
              <Line
                type="monotone"
                dataKey="totalCost"
                name="Total Cost"
                stroke="#ef4444"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 6, stroke: '#ef4444', strokeWidth: 2 }}
              />

              {/* Breakeven point marker */}
              {breakevenPoint && (
                <ReferenceDot
                  x={breakevenPoint.kg}
                  y={breakevenPoint.revenue}
                  r={8}
                  fill="#f59e0b"
                  stroke="#fff"
                  strokeWidth={2}
                  label={{
                    value: 'BE',
                    position: 'top',
                    style: { fontSize: 10, fontWeight: 'bold', fill: '#f59e0b' }
                  }}
                />
              )}

              {/* Current position marker */}
              {currentPoint && currentPoint.kg > 0 && (
                <ReferenceDot
                  x={currentPoint.kg}
                  y={currentPoint.revenue}
                  r={8}
                  fill="#6366f1"
                  stroke="#fff"
                  strokeWidth={2}
                  label={{
                    value: 'Now',
                    position: 'top',
                    style: { fontSize: 10, fontWeight: 'bold', fill: '#6366f1' }
                  }}
                />
              )}

              {/* Breakeven vertical reference line */}
              {breakevenPoint && (
                <ReferenceLine
                  x={breakevenPoint.kg}
                  stroke="#f59e0b"
                  strokeDasharray="3 3"
                  strokeOpacity={0.5}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}

        {/* Legend for markers */}
        {canShowChart && (
          <div className="flex items-center justify-center gap-6 mt-4 text-sm text-neutral-600">
            {breakevenPoint && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <span>Breakeven Point</span>
              </div>
            )}
            {currentPoint && currentPoint.kg > 0 && (
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-indigo-500" />
                <span>Current Position</span>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

/**
 * BreakevenChart Skeleton
 */
export function BreakevenChartSkeleton({ className = '' }) {
  return (
    <Card className={className}>
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-80 w-full" />
      </CardContent>
    </Card>
  )
}
